/**
 * JiraImportModal — Importa Requirements desde issues de Jira (1 issue = 1 Requirement).
 *
 * Flujo:
 * 1. Usuario arma un JQL opcional + tipos de issue
 * 2. POST .../import-jira/preview — trae issues candidatos con action create|update
 * 3. Usuario selecciona un subset y confirma
 * 4. POST .../import-jira — re-consulta cada issue en Jira y hace upsert por external_ref
 */
import React, { useState } from 'react';
import { requirements as reqApi } from '../../services/api';
import type { JiraIssuePreview } from '../../services/api';

const OVERLAY: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const MODAL: React.CSSProperties = {
  width: 680, maxHeight: '88vh', borderRadius: 16,
  background: 'var(--color-background-primary)',
  border: '1px solid var(--color-border-secondary)',
  display: 'flex', flexDirection: 'column', overflow: 'hidden',
};
const BTN_PRIMARY: React.CSSProperties = {
  padding: '9px 20px', borderRadius: 8, border: 'none',
  background: '#3B8BD4', color: '#fff', cursor: 'pointer',
  fontSize: 13, fontWeight: 600,
};
const BTN_SECONDARY: React.CSSProperties = {
  padding: '9px 16px', borderRadius: 8,
  border: '1px solid var(--color-border-secondary)',
  background: 'transparent', cursor: 'pointer',
  fontSize: 13, color: 'var(--color-text-secondary)',
};
const INPUT: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: 8,
  border: '1px solid var(--color-border-secondary)',
  background: 'var(--color-background-primary)', color: 'var(--color-text-primary)',
  fontSize: 13,
};

const ISSUE_TYPES = ['Story', 'Task', 'Bug'];

interface JiraImportModalProps {
  versionId: string;
  onClose: () => void;
  onImported: () => void;
}

export function JiraImportModal({ versionId, onClose, onImported }: JiraImportModalProps) {
  const [step, setStep] = useState<'search' | 'preview' | 'done'>('search');
  const [jql, setJql] = useState('');
  const [issueTypes, setIssueTypes] = useState<string[]>(['Story', 'Task', 'Bug']);
  const [maxResults, setMaxResults] = useState(100);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issues, setIssues] = useState<JiraIssuePreview[]>([]);
  const [jqlUsed, setJqlUsed] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ imported: number; updated: number } | null>(null);

  const toggleType = (t: string) => {
    setIssueTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  };

  const toggleSelected = (ref: string) => {
    setSelected(prev => { const n = new Set(prev); n.has(ref) ? n.delete(ref) : n.add(ref); return n; });
  };

  const search = async () => {
    setLoading(true); setError(null);
    try {
      const res = await reqApi.previewJiraImport(versionId, {
        jql: jql.trim() || undefined, issue_types: issueTypes, max_results: maxResults,
      });
      setIssues(res.issues);
      setJqlUsed(res.jql_used);
      setSelected(new Set(res.issues.map(i => i.external_ref)));
      setStep('preview');
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally { setLoading(false); }
  };

  const confirm = async () => {
    setImporting(true); setError(null);
    try {
      const res = await reqApi.confirmJiraImport(versionId, Array.from(selected));
      setResult({ imported: res.imported, updated: res.updated });
      setStep('done');
      onImported();
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally { setImporting(false); }
  };

  return (
    <div style={OVERLAY} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={MODAL}>
        {/* Header */}
        <div style={{ padding: '16px 22px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text-primary)' }}>🔗 Importar desde Jira</div>
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>1 issue de Jira → 1 requerimiento</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: 'var(--color-text-tertiary)' }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px' }}>
          {error && (
            <div style={{ padding: '10px 14px', borderRadius: 8, background: '#FCEBEB', border: '1px solid #F7C1C1', color: '#791F1F', fontSize: 12, marginBottom: 16 }}>
              ⚠ {error}
            </div>
          )}

          {step === 'search' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 6 }}>
                  JQL personalizado (opcional)
                </label>
                <textarea
                  style={{ ...INPUT, minHeight: 60, resize: 'vertical', fontFamily: 'monospace' }}
                  value={jql}
                  onChange={e => setJql(e.target.value)}
                  placeholder='Ej: project = "PROJ" AND status = "To Do" ORDER BY key ASC'
                />
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                  Si se deja vacío, se arma un JQL con el proyecto configurado y los tipos de issue seleccionados.
                </div>
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 6 }}>
                  Tipos de issue
                </label>
                <div style={{ display: 'flex', gap: 12 }}>
                  {ISSUE_TYPES.map(t => (
                    <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--color-text-primary)', cursor: 'pointer' }}>
                      <input type="checkbox" checked={issueTypes.includes(t)} onChange={() => toggleType(t)} />
                      {t}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 6 }}>
                  Máximo de resultados
                </label>
                <input
                  type="number" min={1} max={500} style={{ ...INPUT, width: 120 }}
                  value={maxResults} onChange={e => setMaxResults(Number(e.target.value) || 100)}
                />
              </div>
            </div>
          )}

          {step === 'preview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', fontFamily: 'monospace', background: 'var(--color-background-secondary)', padding: '6px 10px', borderRadius: 6 }}>
                {jqlUsed}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  {issues.length} issue{issues.length !== 1 ? 's' : ''} encontrado{issues.length !== 1 ? 's' : ''} · {selected.size} seleccionado{selected.size !== 1 ? 's' : ''}
                </span>
                <button
                  onClick={() => setSelected(selected.size === issues.length ? new Set() : new Set(issues.map(i => i.external_ref)))}
                  style={{ border: 'none', background: 'transparent', color: '#3B8BD4', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
                >
                  {selected.size === issues.length ? 'Deseleccionar todo' : 'Seleccionar todo'}
                </button>
              </div>

              {issues.length === 0 ? (
                <div style={{ padding: '30px 0', textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 13 }}>
                  No se encontraron issues con esos criterios.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {issues.map(issue => (
                    <label key={issue.external_ref} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--color-border-tertiary)', cursor: 'pointer' }}>
                      <input type="checkbox" checked={selected.has(issue.external_ref)} onChange={() => toggleSelected(issue.external_ref)} style={{ marginTop: 3 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-tertiary)' }}>{issue.external_ref}</span>
                          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{issue.title}</span>
                        </div>
                        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                          <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: issue.action === 'create' ? '#E1F5EE' : '#E6F1FB', color: issue.action === 'create' ? '#0F6E56' : '#185FA5' }}>
                            {issue.action === 'create' ? 'nuevo' : 'actualiza'}
                          </span>
                          <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 10, background: 'var(--color-background-secondary)', color: 'var(--color-text-tertiary)' }}>{issue.issue_type}</span>
                          <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 10, background: 'var(--color-background-secondary)', color: 'var(--color-text-tertiary)' }}>{issue.priority}</span>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 'done' && result && (
            <div style={{ textAlign: 'center', padding: '30px 0' }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 6 }}>Importación completada</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                {result.imported} creado{result.imported !== 1 ? 's' : ''} · {result.updated} actualizado{result.updated !== 1 ? 's' : ''}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--color-border-tertiary)', display: 'flex', gap: 10, justifyContent: 'flex-end', background: 'var(--color-background-primary)' }}>
          {step === 'search' && (
            <>
              <button onClick={onClose} style={BTN_SECONDARY}>Cancelar</button>
              <button onClick={search} disabled={loading} style={{ ...BTN_PRIMARY, opacity: loading ? 0.6 : 1 }}>
                {loading ? 'Buscando...' : 'Buscar'}
              </button>
            </>
          )}
          {step === 'preview' && (
            <>
              <button onClick={() => setStep('search')} style={BTN_SECONDARY}>← Cambiar búsqueda</button>
              <button
                onClick={confirm}
                disabled={importing || selected.size === 0}
                style={{ ...BTN_PRIMARY, opacity: importing || selected.size === 0 ? 0.5 : 1, cursor: importing || selected.size === 0 ? 'not-allowed' : 'pointer' }}
              >
                {importing ? 'Importando...' : `Importar seleccionados (${selected.size})`}
              </button>
            </>
          )}
          {step === 'done' && (
            <button onClick={onClose} style={BTN_PRIMARY}>Cerrar</button>
          )}
        </div>
      </div>
    </div>
  );
}
